import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AddInIds from "@/constants/AddInIds";
import { useScanList } from "@/hooks/assetScan/useScanList";
import { ScanAsset, Stored } from "@/models/assetScan/AssetScanTypes";

interface Props {
  serial?: string;
  canWrite: boolean;
  busy: boolean;
  error?: string;
  onAssign: (asset: Stored<ScanAsset>) => void;
  onCancel: () => void;
}

/** A tag nobody knows yet: pick the asset it is stuck to; its tag is then written onto the NFC tag. */
export function AssignTag({ serial, canWrite, busy, error, onAssign, onCancel }: Props) {
  const { t } = useTranslation();
  const { data: assets = [], isLoading } = useScanList<ScanAsset>(AddInIds.assetScanAssets);
  const [picked, setPicked] = useState<Stored<ScanAsset> | null>(null);

  return (
    <Stack spacing={2}>
      <Alert severity="info">{t("assetScan.scan.unknownTag", { serial: serial ?? "–" })}</Alert>
      <Autocomplete
        options={assets}
        loading={isLoading}
        value={picked}
        onChange={(_, row) => setPicked(row)}
        getOptionLabel={row => `${row.details.name} (${row.details.tag})`}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={params => <TextField {...params} label={t("assetScan.scan.pickAsset")} />}
      />
      {picked && canWrite && <Alert severity="warning">{t("assetScan.scan.holdToWrite")}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" fullWidth onClick={onCancel} disabled={busy}>
          {t("common.cancel")}
        </Button>
        <Button variant="contained" fullWidth disabled={!picked || busy} onClick={() => picked && onAssign(picked)}>
          {canWrite ? t("assetScan.scan.writeTag") : t("assetScan.scan.assign")}
        </Button>
      </Stack>
    </Stack>
  );
}
